import React, { FormEvent, useEffect, useId, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import { axiosInstance } from "../../utils/api";
import {
  getA9Data,
  getA9ErrorMessage,
  MigrationVerifyConfirmData,
  MigrationVerifyPeekData,
} from "../../utils/a9-envelope";
import * as AuthActions from "../auth/store/auth.actions";
import styles from "./platform.module.scss";

type VerifyState = "loading" | "ready" | "submitting" | "error";

export const PlatformMigrationVerifyPage = () => {
  const formId = useId();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = searchParams.get("token");

  const [state, setState] = useState<VerifyState>("loading");
  const [message, setMessage] = useState("");
  const [accountName, setAccountName] = useState("");
  const [accountEmail, setAccountEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setMessage("Verification link is missing or invalid.");
      return;
    }

    let cancelled = false;

    const validateToken = async () => {
      try {
        const response = await axiosInstance.get(`/platform/verify-migration/${token}`);
        if (cancelled) {
          return;
        }

        const peekData = getA9Data<MigrationVerifyPeekData>(response.data);
        if (peekData) {
          setAccountName(peekData.name || "");
          setAccountEmail(peekData.email || "");
          setState("ready");
          return;
        }

        setState("error");
        setMessage(getA9ErrorMessage(response.data, "Verification link is invalid or expired."));
      } catch (error: any) {
        if (cancelled) {
          return;
        }
        setState("error");
        setMessage(getA9ErrorMessage(error?.response?.data, "Verification link is invalid or expired."));
      }
    };

    validateToken();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token || state !== "ready") {
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters.", { position: "bottom-left" });
      return;
    }

    if (password !== passwordConfirmation) {
      toast.error("Passwords do not match.", { position: "bottom-left" });
      return;
    }

    setState("submitting");
    try {
      const response = await axiosInstance.post(`/platform/verify-migration/${token}`, {
        password,
        passwordConfirmation,
      });

      const confirmData = getA9Data<MigrationVerifyConfirmData>(response.data);
      if (confirmData?.accessToken) {
        localStorage.setItem("accessToken", confirmData.accessToken);
        axios.defaults.headers.common.Authorization = `Bearer ${confirmData.accessToken}`;
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${confirmData.accessToken}`;
        dispatch(AuthActions.AuthenticationSuccess({
          user: confirmData.user,
          msg: confirmData.message,
        }));
        toast.success("Account verified. You can now sign in with your new password.", { position: "bottom-left" });
        navigate("/profile", { replace: true });
        return;
      }

      setState("ready");
      toast.error(getA9ErrorMessage(response.data, "Verification failed."), { position: "bottom-left" });
    } catch (error: any) {
      setState("ready");
      const errorMessage = getA9ErrorMessage(error?.response?.data, error?.message || "Verification failed.");
      toast.error(errorMessage, { position: "bottom-left" });
    }
  };

  return (
    <main className={styles.page}>
      <div className={styles.layout}>
        <section className={styles.panel} aria-live="polite">
          <div className={styles.panelHeader}>
            <div>
              <h2>Spatial migration verification</h2>
              <p>
                {state === "loading" && "Checking your verification link..."}
                {state === "ready" && "Choose a password to verify your email and activate your Exhibitor profile."}
                {state === "submitting" && "Verifying your account and activating your Exhibitor profile..."}
                {state === "error" && message}
              </p>
            </div>
          </div>

          {state === "ready" || state === "submitting" ? (
            <form className={styles.migrationForm} onSubmit={onSubmit} noValidate>
              {accountName || accountEmail ? (
                <p className={styles.formHintText}>
                  Verifying account for {accountName}
                  {accountEmail ? ` (${accountEmail})` : ""}.
                </p>
              ) : null}

              <div className={styles.formFields}>
                <div className={styles.formField}>
                  <label htmlFor={`${formId}-password`} className={styles.formLabel}>
                    Password
                  </label>
                  <input
                    id={`${formId}-password`}
                    name="password"
                    className={styles.formInput}
                    type="password"
                    value={password}
                    placeholder="Choose a password"
                    autoComplete="new-password"
                    onChange={(event) => setPassword(event.target.value)}
                    disabled={state === "submitting"}
                    required
                    minLength={6}
                  />
                </div>

                <div className={styles.formField}>
                  <label htmlFor={`${formId}-password-confirmation`} className={styles.formLabel}>
                    Confirm password
                  </label>
                  <input
                    id={`${formId}-password-confirmation`}
                    name="passwordConfirmation"
                    className={styles.formInput}
                    type="password"
                    value={passwordConfirmation}
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                    onChange={(event) => setPasswordConfirmation(event.target.value)}
                    disabled={state === "submitting"}
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <button
                type="submit"
                className={styles.formSubmit}
                disabled={state === "submitting" || !password || !passwordConfirmation}
              >
                {state === "submitting" ? "Verifying..." : "Verify email and activate Exhibitor profile"}
              </button>
            </form>
          ) : null}

          {state === "error" ? (
            <p>
              Return to the{" "}
              <Link to="/platform?tab=spatial">Spatial migration form</Link>
              {" "}or{" "}
              <Link to="/auth/login">sign in</Link>
              {" "}if you already have an account.
            </p>
          ) : null}
        </section>
      </div>
    </main>
  );
};

export default PlatformMigrationVerifyPage;
